import React from 'react';
import { ContextualHelpPanel } from './ContextualHelpPanel';
import { HELP_CONTENT } from './help-content';
import type { FieldId } from './help-content';

interface CredentialFieldProps {
  /** The field ID — must match a key in HELP_CONTENT */
  fieldId: FieldId;
  /** Field label text */
  label: string;
  /** Current input value */
  value: string;
  /** Change handler */
  onChange: (value: string) => void;
  /** Input placeholder */
  placeholder?: string;
  /** Whether the input is a password/secret field */
  isSecret?: boolean;
  /** Which field's help panel is currently open (single-panel-open logic) */
  openHelpId: FieldId | null;
  /** Callback to toggle help panel — parent manages which is open */
  onToggleHelp: (fieldId: FieldId) => void;
  /** Whether the field is disabled */
  disabled?: boolean;
}

/**
 * Credential input field with inline contextual help.
 * Renders: label + ? icon | input | expandable help panel.
 * Only one panel open at a time — controlled by parent via openHelpId.
 */
export const CredentialField: React.FC<CredentialFieldProps> = ({
  fieldId,
  label,
  value,
  onChange,
  placeholder,
  isSecret = true,
  openHelpId,
  onToggleHelp,
  disabled = false,
}) => {
  const helpContent = HELP_CONTENT[fieldId];
  const isOpen = openHelpId === fieldId;

  return (
    <div style={{ marginBottom: 16 }}>
      {/* Label row with ? icon */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: 6,
      }}>
        <label style={{
          fontSize: 'var(--text-xs, 11px)',
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          color: 'var(--text-secondary, #a3a3a3)',
        }}>
          {label}
        </label>
        <button
          type="button"
          onClick={() => onToggleHelp(fieldId)}
          title={isOpen ? 'Close help' : 'How to get this'}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: '2px 6px',
            borderRadius: 4,
            fontSize: 14,
            fontWeight: 700,
            lineHeight: 1,
            color: isOpen ? 'var(--orange, #00E5FF)' : 'var(--text-muted, #737373)',
            opacity: isOpen ? 1 : 0.7,
            transition: 'opacity 150ms, color 150ms',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.opacity = '1'; }}
          onMouseLeave={(e) => { if (!isOpen) e.currentTarget.style.opacity = '0.7'; }}
        >
          {'\u24D8'}
        </button>
      </div>

      {/* Input field */}
      <input
        type={isSecret ? 'password' : 'text'}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        style={{
          width: '100%',
          padding: '8px 12px',
          background: 'var(--bg-input, rgba(255,255,255,0.05))',
          border: '1px solid var(--border, rgba(255,255,255,0.1))',
          borderRadius: 6,
          color: 'var(--text-primary, #e5e5e5)',
          fontSize: 'var(--text-sm, 13px)',
          outline: 'none',
          boxSizing: 'border-box',
        }}
      />

      {/* Contextual help panel — expands below */}
      {helpContent && (
        <ContextualHelpPanel content={helpContent} isOpen={isOpen} />
      )}
    </div>
  );
};
